import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatDoctorName } from "@/utils/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, Stethoscope, Calendar, TrendingUp, FlaskConical,
  Package, Clock, CheckCircle, AlertTriangle, UserCheck, ArrowRight, Shield
} from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayAppts, setTodayAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/appointments/today"),
    ]).then(([statsRes, apptRes]) => {
      setStats(statsRes.data);
      setTodayAppts(apptRes.data.data.slice(0, 8));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const statCards = stats ? [
    { label: t("total_patients"), value: stats.total_patients, icon: Users, color: "bg-sky-50 text-sky-700", iconBg: "bg-sky-100" },
    { label: t("total_doctors"), value: stats.total_doctors, icon: Stethoscope, color: "bg-emerald-50 text-emerald-700", iconBg: "bg-emerald-100" },
    { label: t("today_appointments"), value: stats.today_appointments, icon: Calendar, color: "bg-violet-50 text-violet-700", iconBg: "bg-violet-100" },
    { label: t("revenue_today"), value: `₹${stats.revenue_today?.toLocaleString() || 0}`, icon: TrendingUp, color: "bg-amber-50 text-amber-700", iconBg: "bg-amber-100" },
  ] : [];

  const statusColors = {
    waiting: "bg-amber-100 text-amber-800",
    in_consultation: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-rose-100 text-rose-800",
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-sans">
          {t("welcome")}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-slate-500 text-sm mt-1">Here's what's happening at your clinic today.</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color, iconBg }) => (
          <Card key={label} className="border-slate-200 stat-card cursor-default" data-testid={`stat-card-${label}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
                  <p className={`text-2xl font-bold mt-1 font-sans ${color.split(" ")[1]}`}>{value}</p>
                </div>
                <div className={`w-11 h-11 ${iconBg} rounded-xl flex items-center justify-center`}>
                  <Icon size={20} className={color.split(" ")[1]} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Claims Alert */}
      {stats && stats.pending_claims > 0 && user?.role === "admin" && (
        <button
          onClick={() => navigate("/billing?filter=pending")}
          className="w-full group bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition-all text-left"
          data-testid="pending-claims-widget"
        >
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield size={22} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-bold text-amber-900 font-sans">{stats.pending_claims}</span>
              <span className="text-sm text-amber-900">insurance claim{stats.pending_claims !== 1 ? "s" : ""} awaiting approval</span>
            </div>
            <p className="text-xs text-amber-700 mt-0.5">Review and approve/reject to unblock patient billing</p>
          </div>
          <div className="flex items-center gap-1 text-sm text-amber-800 font-medium group-hover:gap-2 transition-all flex-shrink-0">
            Review <ArrowRight size={14} />
          </div>
        </button>
      )}

      {/* Secondary Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Waiting", value: stats.waiting_patients, icon: Clock, color: "text-amber-600" },
            { label: "In Consultation", value: stats.in_consultation, icon: UserCheck, color: "text-blue-600" },
            { label: "Completed Today", value: stats.completed_today, icon: CheckCircle, color: "text-emerald-600" },
            { label: "Low Stock Alerts", value: stats.low_stock_alerts, icon: AlertTriangle, color: "text-rose-600" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
              <Icon size={18} className={color} />
              <div>
                <p className="text-xs text-slate-500">{label}</p>
                <p className={`text-lg font-bold font-sans ${color}`}>{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Module Access */}
      {user?.role === "admin" && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 font-sans mb-3">Quick Access</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Patients", path: "/patients", icon: Users, color: "bg-sky-50 text-sky-700 hover:bg-sky-100" },
              { label: "Doctors", path: "/doctors", icon: Stethoscope, color: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" },
              { label: "Appointments", path: "/appointments", icon: Calendar, color: "bg-violet-50 text-violet-700 hover:bg-violet-100" },
              { label: "Lab Tests", path: "/lab", icon: FlaskConical, color: "bg-teal-50 text-teal-700 hover:bg-teal-100" },
              { label: "Inventory", path: "/inventory", icon: Package, color: "bg-amber-50 text-amber-700 hover:bg-amber-100" },
              { label: "Billing", path: "/billing", icon: TrendingUp, color: "bg-rose-50 text-rose-700 hover:bg-rose-100" },
            ].map(({ label, path, icon: Icon, color }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white transition-colors duration-150 cursor-pointer ${color.split(" ").slice(2).join(" ")}`}
                data-testid={`quick-access-${label.toLowerCase()}`}
              >
                <div className={`w-10 h-10 rounded-lg ${color.split(" ").slice(0,2).join(" ")} flex items-center justify-center`}>
                  <Icon size={18} />
                </div>
                <span className="text-xs font-medium text-slate-700">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Today's Queue */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900 font-sans">Today's Queue</h2>
          <Button variant="ghost" size="sm" className="text-emerald-700 hover:text-emerald-800 gap-1" onClick={() => navigate("/appointments")} data-testid="view-all-appts-btn">
            View All <ArrowRight size={14} />
          </Button>
        </div>
        {todayAppts.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <div className="text-center">
              <Calendar size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No appointments today</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {todayAppts.map((appt) => (
              <div key={appt.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`appt-row-${appt.id}`}>
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <span className="text-xs font-bold text-emerald-700">#{appt.token_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{appt.patient_name}</p>
                  <p className="text-xs text-slate-500 truncate">{formatDoctorName(appt.doctor_name)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {appt.is_emergency && (
                    <Badge className="bg-rose-100 text-rose-700 text-xs">Emergency</Badge>
                  )}
                  <Badge className={`text-xs ${statusColors[appt.status] || "bg-slate-100 text-slate-700"}`}>
                    {appt.status?.replace("_", " ")}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Stethoscope size={18} className="text-emerald-700" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Available Doctors</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 font-sans">{stats.available_doctors}</p>
            <p className="text-xs text-slate-500 mt-1">of {stats.total_doctors} total</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center">
                <Building2Icon size={18} className="text-violet-700" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Departments</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 font-sans">{stats.total_departments}</p>
            <p className="text-xs text-slate-500 mt-1">Active departments</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                <FlaskConical size={18} className="text-amber-700" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Pending Tests</p>
            </div>
            <p className="text-3xl font-bold text-slate-900 font-sans">{stats.pending_lab_tests}</p>
            <p className="text-xs text-slate-500 mt-1">Awaiting results</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Local component since we don't import it
function Building2Icon({ size, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className}>
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
